import { useState } from 'react';
import { useNavigate } from 'react-router';
import { paths } from '../../../common/constants/pathVariables.js';
import { DeviceChangeOption } from '../../../common/enums';
import { callUiApi, validateNumbers } from '../../../common/fetch';
import { DeliveryType } from '../../../generated/api/deliveryType';
import { Order } from '../../../generated/api/order';
import { OrderItemUnit } from '../../../generated/api/orderItemUnit';
import { createOrderPrivateMethod } from '../../../generated/api/uiApiMethods';

import { getActiveAccountMasterId } from '../../../selfservice/common/localStorageUtils';
import { getCommercialProductForGuid, getOnlineModelForBasketItem } from '../../ShoppingBasket/shoppingBasketUtils';
import { buildValidatedAddOnsForBasketItem } from '../utils/addOnValidationUtils';
import { saveFormDataToSession } from '../utils/cardPaymentUtils.js';
import { mapOrderItems } from '../utils/orderMappingUtils';

import { calculateOrderTotals, handleEppRedeem, requireValue } from '../utils/placeOrderUtils';
import type { ShoppingBasketType } from '../../../common/types/shoppingBasket';
import type { OnlineModel } from '../../../generated/api/onlineModel';
import type { OrderItem } from '../../../generated/api/orderItem';
import type { PostOrderRequest } from '../../../generated/api/postOrderRequest';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { AuthenticatedUser } from '../../../public/site/authState';
import type { CampaignData, PlaceOrderFormData } from '../placeOrderTypes';

interface UsePlaceOrderProps {
  shoppingBasket: ShoppingBasketType;
  authenticatedUser: AuthenticatedUser;
  onlineModels: OnlineModel[];
  businessId: string;
  campaignData?: CampaignData;
  associations: AssociationRecord[];
  dependencies: DependencyRecord[];
  onNumbersUnavailable?: (unavailableNumbers: string[]) => void;
  onError?: (errorMessage: string) => void;
  onCreditCheckFailure?: () => void;
}

interface UsePlaceOrderReturn {
  isSubmittingOrder: boolean;
  submitOrder: (data: PlaceOrderFormData) => Promise<void>;
}

export const usePlaceOrder = ({
  shoppingBasket,
  authenticatedUser,
  onlineModels,
  businessId,
  campaignData,
  associations,
  dependencies,
  onNumbersUnavailable,
  onError,
  onCreditCheckFailure,
}: UsePlaceOrderProps): UsePlaceOrderReturn => {
  const navigate = useNavigate();
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);

  const submitOrder = async (data: PlaceOrderFormData): Promise<void> => {
    setIsSubmittingOrder(true);

    try {
      // 1. Validate add-ons before submission
      const validatedData: PlaceOrderFormData = {
        ...data,
        products: {
          placeOrderItemUnits: data.products.placeOrderItemUnits.map(unit => {
            const basketItem = shoppingBasket.items?.find(item => item.id === unit.basketItemId);
            if (!basketItem) {
              return unit;
            }

            const onlineModel = getOnlineModelForBasketItem(onlineModels, basketItem);
            if (!onlineModel) {
              return unit;
            }

            const commercialProduct = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, onlineModel);
            if (!commercialProduct) {
              return unit;
            }

            // Re-validate addons with CloudSense rules (updates attributes, enforces mandatory deps)
            // but preserve the user's addon CODES — don't let CloudSense re-add removed OPTIONAL addons.
            const validatedAddons = buildValidatedAddOnsForBasketItem(
              basketItem,
              unit.basketItemIndex,
              commercialProduct,
              onlineModel,
              associations,
              dependencies
            );
            const userAddonCodes = new Set(unit.addons.map(a => a.addOnCode));
            return {
              ...unit,
              addons: validatedAddons.filter(addon => userAddonCodes.has(addon.addOnCode)),
            };
          }),
        },
      };

      // 2. Validate required fields
      const shoppingCartId = requireValue(shoppingBasket.id, 'Shopping basket ID is missing');
      const paymentType = requireValue(validatedData.payment.paymentType, 'Payment type is required');
      const accountMasterId = requireValue(getActiveAccountMasterId(), 'Account master ID is missing');
      const contactId = requireValue(authenticatedUser?.contact?.contactId, 'ContactId missing');

      // 3. Build order items from basket mapping
      const orderItems: OrderItem[] = mapOrderItems(
        onlineModels,
        requireValue(shoppingBasket.items, 'Empty basket'),
        validatedData.products.placeOrderItemUnits,
        campaignData
      );
      requireValue(orderItems.length, 'No items to order');

      // 4. Validate phone number availability for NEW numbers
      const newNumbers = validatedData.products.placeOrderItemUnits
        .filter(unit => unit.mobileNumber && unit.numberPorting === OrderItemUnit.NumberPortingEnum.NEW_NUMBER)
        .map(unit => unit.mobileNumber!);

      if (newNumbers.length > 0) {
        const validation = await validateNumbers({ numbers: newNumbers });
        if (!validation.allNumbersAvailable) {
          setIsSubmittingOrder(false);
          if (onNumbersUnavailable) {
            onNumbersUnavailable(validation.notAvailableNumbers || []);
          }
          return;
        }
      }

      // 5. Handle EPP Device Change - if REDEEM, call EPP Redeem API first
      const deviceChangeUnit = validatedData.products.placeOrderItemUnits.find(
        unit => unit.deviceChange?.replacedSubscriptionId
      );
      const deviceChange = deviceChangeUnit?.deviceChange;

      if (deviceChange?.deviceChangeOption === DeviceChangeOption.REDEEM) {
        /**
         * Handle EPP Redeem API call for device change with REDEEM option.
         * This must be called before creating the order.
         */
        await handleEppRedeem(deviceChange);
      }

      // 6. Calculate totals - split by subscription vs non-subscription items
      const totals = calculateOrderTotals(shoppingBasket, onlineModels);

      // 7. Do the Order payload
      const order: Omit<Order, 'orderId'> = {
        shoppingCartId,
        contactId,
        businessId,
        accountMasterId,
        totalOneTimeCharge: totals.totalOneTimeCharge,
        totalFixedTermCharge: totals.totalFixedTermCharge,
        deliveryCharge: validatedData.delivery.deliveryPriceInCents ?? 0,
        totalMonthlyRecurringCharge: totals.totalMonthlyRecurringCharge,
        totalSubscriptionMonthlyRecurringCharge: totals.totalSubscriptionMonthlyRecurringCharge,
        totalSubscriptionOneTimeCharge: totals.totalSubscriptionOneTimeCharge,
        totalSalesProductsMonthlyRecurringCharge: totals.totalSalesProductsMonthlyRecurringCharge,
        totalSalesProductsOneTimeCharge: totals.totalSalesProductsOneTimeCharge,
        paymentType,
        billingAccountId: validatedData.payment.billingAccountId,
        deliveryType: validatedData.delivery.deliveryType ?? DeliveryType.NONE,
        shipmentType: validatedData.delivery.shipmentType ?? Order.ShipmentTypeEnum.TOTAL_DELIVERY,
        orderItems,
        // Clear deliveryAddress when deliveryType is NONE (e.g. eSIM) — backend rejects it otherwise
        ...(validatedData.delivery.deliveryType !== DeliveryType.NONE && {
          deliveryAddress: validatedData.delivery.deliveryAddress && {
            ...validatedData.delivery.deliveryAddress,
            pickupPoint: validatedData.delivery.pickupPoint,
          },
        }),
        voucherCode: campaignData?.voucherCode,
      };

      // 5. Post
      const postOrderRequest: PostOrderRequest = { order: order as Order };
      const response = await callUiApi(createOrderPrivateMethod(), {
        body: JSON.stringify(postOrderRequest),
        noThrow: true, // Allow non-2xx responses (like 402 credit check failure) to be returned instead of thrown
      });

      // 6. Response handling
      const isCardPayment = paymentType === Order.PaymentTypeEnum.CARD;
      const isBillingPayment = paymentType === Order.PaymentTypeEnum.BILLING_ACCOUNT;

      // Check for credit check failure (402)
      if (response.status === 402) {
        onCreditCheckFailure?.();
        return;
      }

      // Card payment: 200 with forwardUrl -> save session, redirect to Nets
      if (isCardPayment && response.status === 200) {
        const responseData = await response.json();

        if (responseData.forwardUrl && responseData.transactionId && responseData.orderId) {
          // Save form data to session before redirect (for thank you page)
          saveFormDataToSession({
            formData: validatedData,
            transactionId: responseData.transactionId,
            orderId: responseData.orderId,
            timestamp: Date.now(),
          });

          // Redirect to Nets payment page (full page navigation)
          window.location.href = responseData.forwardUrl;
          // Don't set isSubmittingOrder to false - we're leaving the page
          return;
        }
      }

      // Billing account: 201 or 202 is success
      if (isBillingPayment && (response.status === 201 || response.status === 202)) {
        let orderId = shoppingCartId;
        if (response.status === 201) {
          const responseData = await response.json();
          orderId = responseData.createdOrderId ?? shoppingCartId;
        }

        // Save form data to session for thank you page (with actual orderId)
        saveFormDataToSession({
          formData: validatedData,
          transactionId: '',
          orderId,
          timestamp: Date.now(),
        });

        // Navigate to thank you page (basket is cleared in PlaceOrderThankYou after render)
        navigate(paths.PLACE_ORDER_THANK_YOU);
        return;
      }

      // Any other response is an error
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message ?? response.statusText ?? 'Unknown error';
      onError?.(errorMessage);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      onError?.(errorMessage);
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  return { isSubmittingOrder, submitOrder };
};
